
import { computed, ElementRef, inject, Injectable, Signal, signal, WritableSignal, DOCUMENT } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class ViewportObserverService {
  public readonly isInViewport: Signal<boolean>;
  public readonly isPartiallyViewport: Signal<boolean>;

  private readonly isElementInViewport: WritableSignal<boolean> = signal(false);
  private readonly isElementPartiallyInViewport: WritableSignal<boolean> = signal(false);
  private readonly isFooterInViewport: WritableSignal<boolean> = signal(false);

  private intersectionObserverInViewport?: IntersectionObserver;
  private footerObserver?: IntersectionObserver;

  private readonly document: Document = inject(DOCUMENT);

  public constructor() {
    this.isInViewport = computed(() => {
      return this.isFooterInViewport() || this.isElementInViewport();
    });

    this.isPartiallyViewport = computed(() => {
      return this.isFooterInViewport() || this.isElementPartiallyInViewport();
    });
  }

  public observeViewport(elementRef: ElementRef<HTMLElement>): void {
    this.disconnectObservers();

    this.observeElement(elementRef.nativeElement);
    this.observeFooterNavigationCol();
  }

  public disconnectObservers(): void {
    this.intersectionObserverInViewport?.disconnect();
    this.footerObserver?.disconnect();
  }

  private observeElement(element: HTMLElement): void {
    this.intersectionObserverInViewport = new IntersectionObserver(
      this.isElementInViewportCallback.bind(this),
      {
        threshold: [0.5, 1],
      },
    );
    this.intersectionObserverInViewport.observe(element);
  }

  private observeFooterNavigationCol(): void {
    const footerElement: Element | null = this.document.querySelector('ui-cep-footer');

    if (!footerElement) {
      return;
    }

    this.footerObserver = new IntersectionObserver(
      this.isFooterInViewportCallback.bind(this),
    );

    this.footerObserver.observe(footerElement);
  }

  private isFooterInViewportCallback(entries: IntersectionObserverEntry[]): void {
    const entry: IntersectionObserverEntry = entries[0];

    this.isFooterInViewport.set(entry.isIntersecting);
  }

  private isElementInViewportCallback(entries: IntersectionObserverEntry[]): void {
    const entry: IntersectionObserverEntry = entries[0];

    this.isElementInViewport.set(entry.intersectionRatio === 1);
    this.isElementPartiallyInViewport.set(entry.intersectionRatio >= 0.5);
  }
}
